import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'librebooks_library';

const defaultState = {
  bookmarks: {},       // bookId → book object
  readingStatus: {},   // bookId → 'want' | 'reading' | 'finished'
  ratings: {},         // bookId → 1-5
  recentlyViewed: [],  // array of book objects (max 20)
  searchHistory: [],   // array of query strings (max 10)
};

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    return {
      bookmarks: parsed.bookmarks || {},
      readingStatus: parsed.readingStatus || {},
      ratings: parsed.ratings || {},
      recentlyViewed: Array.isArray(parsed.recentlyViewed) ? parsed.recentlyViewed : [],
      searchHistory: Array.isArray(parsed.searchHistory) ? parsed.searchHistory : [],
    };
  } catch {
    return defaultState;
  }
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage quota exceeded or unavailable — fail silently
  }
}

const LibraryContext = createContext(null);

export function LibraryProvider({ children }) {
  const [library, setLibrary] = useState(() => loadFromStorage());

  // Persist to localStorage whenever state changes
  useEffect(() => {
    saveToStorage(library);
  }, [library]);

  // ── Bookmarks ──────────────────────────────────────────────────────

  const toggleBookmark = useCallback((book) => {
    if (!book || !book.id) return;
    const bookId = String(book.id);
    setLibrary((prev) => {
      const updated = { ...prev.bookmarks };
      if (updated[bookId]) {
        delete updated[bookId];
      } else {
        updated[bookId] = book;
      }
      return { ...prev, bookmarks: updated };
    });
  }, []);

  const isBookmarked = useCallback(
    (bookId) => Boolean(library.bookmarks[String(bookId)]),
    [library.bookmarks]
  );

  // ── Reading Status ─────────────────────────────────────────────────

  const setReadingStatus = useCallback((book, status) => {
    if (!book || !book.id) return;
    const bookId = String(book.id);
    setLibrary((prev) => {
      const updatedStatus = { ...prev.readingStatus };
      const updatedBookmarks = { ...prev.bookmarks };
      if (status === null || status === undefined) {
        delete updatedStatus[bookId];
      } else {
        updatedStatus[bookId] = status;
        // Auto-bookmark when adding to a list
        if (!updatedBookmarks[bookId]) {
          updatedBookmarks[bookId] = book;
        }
      }
      return { ...prev, readingStatus: updatedStatus, bookmarks: updatedBookmarks };
    });
  }, []);

  const getReadingStatus = useCallback(
    (bookId) => library.readingStatus[String(bookId)] || null,
    [library.readingStatus]
  );

  // ── Ratings ────────────────────────────────────────────────────────

  const setRating = useCallback((bookId, rating) => {
    if (!bookId) return;
    const id = String(bookId);
    const clamped = Math.min(5, Math.max(1, Math.round(rating)));
    setLibrary((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, [id]: clamped },
    }));
  }, []);

  const getRating = useCallback(
    (bookId) => library.ratings[String(bookId)] || 0,
    [library.ratings]
  );

  // ── Recently Viewed ────────────────────────────────────────────────

  const addRecentlyViewed = useCallback((book) => {
    if (!book || !book.id) return;
    const bookId = String(book.id);
    setLibrary((prev) => {
      const filtered = prev.recentlyViewed.filter((b) => String(b.id) !== bookId);
      const updated = [book, ...filtered].slice(0, 20);
      return { ...prev, recentlyViewed: updated };
    });
  }, []);

  // ── Search History ─────────────────────────────────────────────────

  const addSearchHistory = useCallback((query) => {
    if (!query || !query.trim()) return;
    const q = query.trim();
    setLibrary((prev) => {
      const filtered = prev.searchHistory.filter((s) => s !== q);
      const updated = [q, ...filtered].slice(0, 10);
      return { ...prev, searchHistory: updated };
    });
  }, []);

  // ── Stats ──────────────────────────────────────────────────────────

  const getStats = useCallback(() => {
    const saved = Object.keys(library.bookmarks).length;
    const reading = Object.values(library.readingStatus).filter((s) => s === 'reading').length;
    const finished = Object.values(library.readingStatus).filter((s) => s === 'finished').length;
    const total = saved;
    return { saved, reading, finished, total };
  }, [library.bookmarks, library.readingStatus]);

  const value = {
    // State
    bookmarks: library.bookmarks,
    readingStatus: library.readingStatus,
    ratings: library.ratings,
    recentlyViewed: library.recentlyViewed,
    searchHistory: library.searchHistory,
    // Bookmark functions
    toggleBookmark,
    isBookmarked,
    // Reading status functions
    setReadingStatus,
    getReadingStatus,
    // Rating functions
    setRating,
    getRating,
    // History functions
    addRecentlyViewed,
    addSearchHistory,
    // Stats
    getStats,
  };

  return (
    <LibraryContext.Provider value={value}>
      {children}
    </LibraryContext.Provider>
  );
}

export function useLibrary() {
  const ctx = useContext(LibraryContext);
  if (!ctx) {
    throw new Error('useLibrary must be used within a LibraryProvider');
  }
  return ctx;
}

export default LibraryContext;
